import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uploadItemTrackingHandler from "@tKgpco/Store/Data/ItemTracking/Handlers/UploadItemTracking";
import React, { useContext, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    uploadItemTrackingHandler,
};

const mapStateToProps = (state: ApplicationState) => ({});

interface CustomWidgetStyles {
    wrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    uploadingButtonLabel?: TypographyPresentationProps;
    uploadFileButtonLabel?: TypographyPresentationProps;
    uploadFileButton?: ButtonPresentationProps;
    downloadTemplateButton?: ButtonPresentationProps;
    fileUploader?: FileUploadPresentationProps;
    title?: TypographyPresentationProps;
}

const styles: CustomWidgetStyles = {
    wrapper: {
        css: css`
            display: flex;
            gap: 20px;
        `,
    },
    title: {
        css: css`
            display: flex;
            align-items: center;
            margin-bottom: 0;
        `,
    },
    uploadFileButton: {
        css: css`
            /* float: right; */
            margin-top: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 10px;
                            width: 100%;
                        `,
                        css`
                            width: initial;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    spinner: {
        size: 22,
        css: css`
            margin-right: 8px;
        `,
    },
    uploadingButtonLabel: {
        css: css`
            font-size: 15px;
            font-weight: bold;
            vertical-align: super;
        `,
    },
    uploadFileButtonLabel: {
        css: css`
            font-size: 15px;
            font-weight: bold;
        `,
    },
    downloadTemplateButton: {
        css: css`
            margin-bottom: 15px;
        `,
        variant: "secondary",
    },
    fileUploader: {
        cssOverrides: {
            formField: css`
                > div {
                    margin-top: 10px !important;

                    [class^="StyledButton"] {
                        right: 30px !important;
                    }
                }
            `,
        },
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ItemTrackingImport: React.FC<Props> = ({ id, uploadItemTrackingHandler }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);

    const [file, setFile] = useState<any>(null);
    const [incorrectFileExtension, setIncorrectFileExtension] = React.useState(false);

    const fileChangeHandler = (event: React.ChangeEvent<any>) => {
        const uploadedFile = event.target.files[0];
        setFile(uploadedFile);
        if (uploadedFile) {
            const fileExtension = getFileExtension(uploadedFile.name);
            const incorrectFileExtension = ["xls", "xlsx", "csv"].indexOf(fileExtension) === -1;
            setIncorrectFileExtension(incorrectFileExtension);
        }
    };

    const uploadFileHandler = () => {
        if (isUploading) {
            return;
        }

        if (!file) {
            return;
        }
        setIsUploading(true);
        uploadItemTrackingHandler({
            file,
            onSuccess: (result: any) => {
                setIsUploading(false);
                setIsOpen(false);
                toasterContext.addToast({
                    body: translate(`${result?.message}. Total Count:{0}`, result.successCount),
                    messageType: "success",
                });
                setFile(null);
            },
            onError: (message: string) => {
                setIsUploading(false);
                setIsOpen(false);
                setFile(null);
                toasterContext.addToast({ body: translate(`${message}`), messageType: "danger" });
            },
        });
    };

    const downloadTemplateHandler = () => {
        window.open(`${window.location.origin}/userfiles/ItemTrackingImport.xlsx`);
    };

    const closeModal = () => {
        setIsOpen(false);
        setFile(null);
    };

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <Typography variant="h2" {...styles.title}>
                    {translate("File Upload")}:
                </Typography>

                <Button onClick={() => setIsOpen(true)} aria-label="Import Item Tracking" title="Import Item Tracking">
                    {translate("Choose File")}
                </Button>
            </StyledWrapper>
            <Modal isOpen={isOpen} handleClose={closeModal} headline="Item Tracking Upload">
                <Button {...styles.downloadTemplateButton} onClick={downloadTemplateHandler}>
                    {translate("Download Template")}
                </Button>
                <FileUpload
                    {...styles.fileUploader}
                    inputRef={fileUploadRef}
                    fileName={file?.name || ""}
                    accept=".xls,.xlsx,.csv"
                    label={translate("Upload a File")}
                    labelPosition="top"
                    error={
                        file && incorrectFileExtension ? translate("File type must be .xls, .xlsx or .csv.") : undefined
                    }
                    onFileChange={fileChangeHandler}
                    uid="accessorialUploadFileUpload"
                />
                <Button
                    {...styles.uploadFileButton}
                    disabled={!file || isUploading || incorrectFileExtension}
                    onClick={uploadFileHandler}
                    data-test-selector="orderUpload_UploadFile"
                    icon={{
                        src: isUploading ? LoadingSpinner : "",
                        position: "right",
                    }}
                >
                    <Typography {...styles.uploadFileButtonLabel}>{translate("Upload File")}</Typography>
                </Button>
            </Modal>
        </>
    );
};

const ItemTrackingImportWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ItemTrackingImport),
    definition: {
        group: "Basic",
    },
};

export default ItemTrackingImportWidget;
